import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import TextInput from "../../common/textfield/TextInput";
import TextArea from "../../common/textfield/TextArea";
import { FormikObj } from "./constant.js";
import { Switch } from "@mui/material";
import { editDocumentReference } from "../../api/FillableDocument.js";
import { useSelector } from "react-redux";
import Select from "react-select";
import { useClinicLocationsQuery } from "../../../hooks/ReactQueryHooks/useClinicLocationsQuery.js";

export function EditResource({ modalShow, handleShow, refreshTable }) {
  const [btnLoading, setBtnLoading] = useState(false);
  const userRoles = useSelector((state) => state?.auth?.user?.["cognito:groups"]);

  // --- derive org id from the record being edited
  const selectedOrgId = useMemo(() => {
    const ref = modalShow?.custodian?.reference; // "Organization/017187"
    if (!ref || typeof ref !== "string") return null;
    const parts = ref.split("/");
    return parts.length === 2 ? parts[1] : null;
  }, [modalShow?.custodian?.reference]);

  // --- fetch ONLY that org's locations
  const {
    data: orgLocations = [],
    isFetching: locLoading,
    isError: locError,
  } = useClinicLocationsQuery({
    orgIds: selectedOrgId ? [selectedOrgId] : [],
    enabled: !!selectedOrgId,
    onSuccess: () => {
      // no-op here; form state is set in the effect below
    },
  });

  // normalize to react-select options
  const clinicLocationOptions = useMemo(
    () =>
      (orgLocations || []).map((l) => ({
        value: l?.id,
        label: l?.name || "Unnamed Location",
      })),
    [orgLocations]
  );

  const handleStatusChange = (event) => {
    const value = event?.target?.checked;
    formik.setFieldValue("status", value === true ? "current" : "superseded");
  };

  const formik = useFormik({
    ...FormikObj,
    onSubmit: async (values) => {
      const selectedLocations = (values.locations || []).map((loc) => ({
        reference: `Location/${loc.value}`,
        display: loc.label,
      }));

      const finalValues = { ...values, locations: selectedLocations };

      try {
        setBtnLoading(true);
        const res = await editDocumentReference(finalValues);
        if (res.status === true) {
          success(res.message);
          handleShow();
          refreshTable();
        } else {
          failed(res.error);
        }
      } catch (err) {
        failed(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err.message
        );
      } finally {
        setBtnLoading(false);
      }
    },
  });

  // Seed the form once modal/data are present.
  useEffect(() => {
    if (!modalShow?.id) return;

    // Format existing locations from the resource
    const formattedLocations =
      modalShow?.location?.map((loc) => ({
        value: (loc.reference || "").split("/")[1],
        label: loc.display || "Unnamed Location",
      })) || [];

    formik.setValues({
      id: modalShow?.id || "",
      name: modalShow?.content?.[0]?.attachment?.title?.trim() || "",
      description: modalShow?.description || "",
      status: modalShow?.status || "",
      organization: modalShow?.custodian?.display || "",
      url: modalShow?.content?.[0]?.attachment?.url || "",
      locations: formattedLocations,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalShow]);

  // If org locations load and current selections contain locations from other orgs,
  // prune them quietly (defensive; shouldn’t happen since org is fixed in edit view).
  useEffect(() => {
    if (!selectedOrgId) return;
    if (!Array.isArray(formik.values.locations)) return;

    const optionIds = new Set(clinicLocationOptions.map((o) => o.value));
    const filtered = formik.values.locations.filter((sel) => optionIds.has(sel.value));

    if (filtered.length !== formik.values.locations.length) {
      formik.setFieldValue("locations", filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicLocationOptions, selectedOrgId]);

  return (
    <Modal
      backdrop={"static"}
      size="lg"
      show={modalShow}
      onHide={handleShow}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="custom-dialog"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="contained-modal-title-vcenter">Edit Resource</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <Row>
            <label>Organization</label>
            <span style={{fontWeight: "bold", paddingBottom: 15}}>{formik.values.organization}</span>
            {/* {userRoles?.includes("Super-Admin") && (
              <Col>
                <TextInput
                  keyField={"organization"}
                  label={"Organization"}
                  formik={formik}
                  placeholder={"Organization"}
                  disabled={true}
                />
              </Col>
            )} */}
            <Col>
              <TextInput
                keyField={"name"}
                label={"Name"}
                formik={formik}
                placeholder={"Document Name"}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <TextArea
                keyField={"description"}
                label={"Description"}
                formik={formik}
                placeholder={"Description..."}
                required={false}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <TextInput keyField={"url"} label={"URL"} formik={formik} placeholder={"URL"} />
            </Col>
          </Row>

          <Row>
            <Col className="field-wrap multiselect">
              <label id="mutiple-select-label"> Select Clinical Location(s)</label>
              <Select
                isDisabled={!selectedOrgId || locLoading}
                isLoading={locLoading}
                options={clinicLocationOptions}
                isMulti
                value={formik.values.locations}
                onChange={(selected) => formik.setFieldValue("locations", selected || [])}
                placeholder={
                  selectedOrgId ? "Select locations" : "Organization missing"
                }
                noOptionsMessage={() =>
                  selectedOrgId
                    ? locError
                      ? "Failed to load locations"
                      : "No locations found"
                    : "Organization missing"
                }
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <label>Status</label>
              <Switch
                name="status"
                onClick={handleStatusChange}
                defaultChecked={modalShow?.status === "current"}
              />
            </Col>
          </Row>

          <div className="btn-wrap" style={{ display: "flex" }}>
            <Button onClick={() => handleShow(false)} variant="secondary" title="Cancel">
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Save
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}